<template>
  <div class="ticket-viewer">
    <button
      v-if="!$apollo.loading && ticketsData.length"
      class="download-fab"
      :disabled="downloading"
      @click="downloadPdf"
    >
      <svg class="download-fab__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 3a1 1 0 0 1 1 1v9.585l3.293-3.292a1 1 0 1 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5a1 1 0 1 1 1.414-1.414L11 13.585V4a1 1 0 0 1 1-1Zm-7 15a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1Z"
        />
      </svg>
      <span>{{ downloading ? downloadingLabel : downloadLabel }}</span>
    </button>
    <div v-if="!$apollo.loading && ticketsData.length" class="tickets">
      <div
        v-for="(t, index) in ticketsData"
        :key="index"
        class="ticket-page"
        :style="{ '--screen-zoom': screenZoom }"
      >
        <!-- FRONT PANEL -->
        <div class="front">
          <div class="front-top">
            <div class="front-top-left">
              <div class="header">
                <img class="logo-big" :src="logoBig" alt="CoreEvent">
                <div class="header-data">
                  <span class="hd">{{ tr.order }} #<b>100{{ orderInfo.id }}</b></span>
                  <span class="hd b">•</span>
                  <span class="hd">{{ tr.ticketId }} #<b>{{ t.accreditationId }}</b></span>
                  <span class="hd b">•</span>
                  <span class="hd">{{ tr.ticket }} <b>{{ index + 1 }}/{{ ticketsData.length }}</b></span>
                </div>
              </div>
              <div class="title-block">
                <div v-if="t.validityBadge" class="validity-badge" :style="{ width: t.validityBadgeWidth }">
                  {{ t.validityBadge }}
                </div>
                <div class="event-title" :style="{ marginTop: t.validityBadge ? '16px' : '0' }">
                  {{ eventTitle }}
                </div>
              </div>
            </div>
            <img v-if="t.qr" class="qr" :src="t.qr" alt="QR">
          </div>
          <div class="front-bottom">
            <div class="organizer-block">
              <div class="organizer-label">{{ tr.organizer }}:</div>
              <div class="organizer-data">
                <b>{{ org.legalName }}</b><b> • </b><span>{{ orgAddress }}</span><b> • </b><span>{{ org.OIB }}</span>
              </div>
              <div v-if="vatMessage" class="vat-msg">{{ vatMessage }}</div>
            </div>
            <div class="coreevent-info">
              <div>{{ tr.ticketPurchasedOn }}:</div>
              <div class="b">{{ tr.coreEventWeb }}</div>
              <div>{{ tr.issuedBy }}: <b>{{ tr.coreEvent }}</b></div>
            </div>
          </div>
        </div>

        <!-- FOLD DIVIDER 2 -->
        <div class="divider">
          <span class="divider-start" />
          <span class="divider-text">2. {{ tr.foldHere }}</span>
          <span class="divider-middle" />
          <span class="divider-circle" />
        </div>

        <!-- BACK PANEL -->
        <div class="back">
          <img v-if="event.cover" class="cover" :src="event.cover" alt="">
          <div class="back-data">
            <div class="back-row">
              <div class="cell" style="width: 141px">
                <div class="text-bold clamp-3">{{ location.venue || '' }}</div>
                <div class="text-normal clamp-2">{{ backAddress }}</div>
              </div>
              <div class="cell left-border" style="width: 108px">
                <div class="text-label">{{ tr.entryFrom }}</div>
                <div class="text-bold">{{ t.entryFromDate }}</div>
                <div class="text-normal">{{ t.entryFromTime }}</div>
              </div>
            </div>
            <div class="back-row">
              <div class="cell" style="width: 55%">
                <div class="text-bold clamp-3" :style="{ fontSize: t.nameFontSize + 'px' }">
                  {{ t.orderItem.name }}
                </div>
                <div v-if="t.ownerName" class="text-bold text-owner">{{ t.ownerName }}</div>
              </div>
              <div v-if="t.showEntryTo" class="cell left-border" style="width: 108px">
                <div class="text-label">{{ tr.entryTo }}</div>
                <div class="text-bold">{{ t.entryToDate }}</div>
                <div v-if="t.entryToTime" class="text-normal">{{ t.entryToTime }}</div>
              </div>
            </div>
            <div class="back-row">
              <div
                v-for="(seat, si) in t.seatInfo"
                :key="seat.key"
                class="cell"
                :class="{ 'left-border': si !== 0 }"
                :style="{ width: seatWidth(si) }"
              >
                <div class="text-label">{{ seat.key }}</div>
                <div class="text-bold clamp-2">{{ seat.value }}</div>
              </div>
              <div v-if="t.showPrice" class="cell" :class="{ 'left-border': t.seatInfo.length }" style="width: 60px">
                <div class="text-label">{{ tr.price }}</div>
                <div class="text-bold clamp-2">{{ t.orderItem.price }} €</div>
                <div v-if="t.vatLine" class="text-small">{{ t.vatLine }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- FOLD DIVIDER 1 -->
        <div class="divider">
          <span class="divider-start" />
          <span class="divider-text">1. {{ tr.foldHere }}</span>
          <span class="divider-middle" />
          <span class="divider-circle" />
        </div>

        <!-- INNER PANEL -->
        <div class="inner">
          <div class="inner-top">
            <div class="how-to-enter">
              <div class="hte-text">
                <div class="hte-title">{{ tr.howToEnterEvent }}:</div>
                <div class="hte-list">
                  <div>A. <b>{{ tr.printTicket }}</b> {{ tr.showAtTheEntrance }}</div>
                  <div>B. <b>{{ tr.showInPdf }}</b> {{ tr.onYourPhone }}</div>
                  <div>
                    C. {{ tr.downloadMobileApp }} (<a
                      class="hte-link"
                      href="https://apps.apple.com/us/app/coreevent/id1481327944"
                      target="_blank"
                      rel="noopener"
                    >iOS</a>
                    /
                    <a
                      class="hte-link"
                      href="https://play.google.com/store/apps/details?id=co.coreevent.app&hl=en&gl=US"
                      target="_blank"
                      rel="noopener"
                    >Android</a>) {{ tr.ticketsInOnePlace }}
                  </div>
                </div>
              </div>
              <img class="scan-visual" :src="scanVisual" alt="">
            </div>
            <div class="messages">
              <div v-if="organizerMessage" class="org-msg-container">
                <div class="org-msg">
                  <div class="org-msg-title">{{ tr.organizerMessage }}</div>
                  <div class="org-msg-content" v-html="organizerMessage" />
                </div>
                <div class="coreevent-msg">{{ tr.coreEventMessage }}</div>
              </div>
              <div class="terms">
                <div class="terms-title">{{ tr.termsOfUse }}</div>
                <div class="terms-text">{{ termsOfUse }}</div>
                <div v-if="!organizerMessage" class="coreevent-msg">{{ tr.coreEventMessage }}</div>
              </div>
            </div>
          </div>
          <img
            v-if="additionalVisual"
            class="additional-visual"
            :src="additionalVisual"
            alt=""
          >
        </div>

        <!-- FOOTER -->
        <div class="footer">
          <div class="footer-core">
            <img class="logo-small" :src="logoSmall" alt="">
            <span class="b">CoreEvent</span>
            <span class="b">•</span>
            <a class="footer-link" href="https://core-event.co/" target="_blank" rel="noopener">www.core-event.co</a>
          </div>
          <div class="footer-right">
            {{ tr.buyTicketsOnline }} <b>{{ tr.fastAndSimple }}</b>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment-timezone'
import QRCode from 'qrcode'
import { GET_ORDER_INFO_BY_UUID } from './../../graphql/order.graphql'
import { TICKET_TRANSLATIONS } from './ticket-pdf-translations'

const round = (value, decimals = 2) => {
  const factor = Math.pow(10, decimals)

  return Math.round(Number(value) * factor) / factor
}

const HIDDEN_PRICE_PROMO_CODE_IDS = [9446]
const SEAT_WIDTHS = { 0: '120px', 1: '90px' }
const TABLE_ROW_LOCATION_ORDER_ITEM_IDS = [26836, 24683]
const SPECIAL_TABLE_LOCATION_EVENT_IDS = [7345, 9022]

export default {
  props: {
    orderUUID: String,
    purchaseIndex: String,
  },
  data() {
    return {
      orderInfo: {},
      event: {},
      org: {},
      location: {},
      lang: 'hr',
      ticketsData: [],
      downloading: false,
      screenZoom: 1,
      logoBig: require('../../../public/assets/core-event-logo-big.png'),
      logoSmall: require('../../../public/assets/core-event-logo-small.png'),
      scanVisual: require('../../../public/assets/scan-visuals.png'),
    }
  },
  computed: {
    tr() {
      return TICKET_TRANSLATIONS[this.lang] || TICKET_TRANSLATIONS.hr
    },
    eventTitle() {
      return this.event.title || ''
    },
    organizerMessage() {
      return this.event.organizerMessage || ''
    },
    additionalVisual() {
      return this.event.metadata && this.event.metadata.additionalVisual
        ? this.event.metadata.additionalVisual
        : ''
    },
    orgAddress() {
      return [this.org.address, this.org.postalCode, this.org.city].filter(Boolean).join(', ')
    },
    backAddress() {
      return [this.location.address1, this.location.postalCode, this.location.city]
        .filter(Boolean)
        .join(', ')
    },
    isTheater() {
      return this.org.type === 'THEATER'
    },
    vatMessage() {
      if (this.org.VATObligor) return ''

      return this.isTheater ? this.tr.nonVatObligatoryMessageTheater : this.tr.nonVatObligatoryMessage
    },
    downloadLabel() {
      return this.lang === 'en' ? 'Download PDF' : 'Preuzmi PDF'
    },
    downloadingLabel() {
      return this.lang === 'en' ? 'Downloading…' : 'Preuzimanje…'
    },
    termsOfUse() {
      let terms = this.tr.termsOfUseDescription
      if (Number(this.event.id) !== 4302) {
        terms += ` ${this.tr.consentToBeRecorded}`
      }

      return terms
    },
  },
  mounted() {
    this.updateZoom()
    window.addEventListener('resize', this.updateZoom)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateZoom)
  },
  methods: {
    // The ticket is authored at a fixed 595px width (react-pdf A4 proportions).
    // On narrow screens scale the whole ticket down so it fits without horizontal
    // scroll. Uses a CSS var consumed only on screen; print keeps its own A4 zoom.
    updateZoom() {
      const TICKET_WIDTH = 595
      const horizontalPadding = window.innerWidth <= 640 ? 24 : 32
      const available = window.innerWidth - horizontalPadding
      this.screenZoom = Math.min(1, available / TICKET_WIDTH)
    },
    async downloadPdf() {
      if (this.downloading) return

      this.downloading = true
      try {
        const params = { lang: this.lang }
        const { data } = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/files/tickets/${this.orderUUID}`,
          { params },
        )
        const byteString = window.atob(data.body)
        const int8Array = new Uint8Array(byteString.length)
        for (let i = 0; i < byteString.length; i++) {
          int8Array[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([int8Array], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `CoreEvent-Ulaznice-100${this.orderInfo.id}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Ticket PDF download failed', err)
      } finally {
        this.downloading = false
      }
    },
    seatWidth(index) {
      return SEAT_WIDTHS[index] || '50px'
    },
    formatDate(date) {
      return moment(date).tz('Europe/Zagreb').format('dddd, DD.MM.YYYY.')
    },
    formatTime(date) {
      return moment(date).tz('Europe/Zagreb').format('HH:mm [h]')
    },
    calculateNameFontSize(text) {
      const name = text || ''
      if (name.length <= 150) return 12
      if (name.length >= 170) return 10

      return 27 - name.length * 0.1
    },
    buildOwnerName(accreditation) {
      const fields = (accreditation && accreditation.fields) || {}

      return [fields.firstName || fields['First name'], fields.lastName || fields['Last name']]
        .filter(Boolean)
        .join(' ')
    },
    buildSeatInfo(orderItem, accreditation) {
      const ticketLocation = accreditation && accreditation.ticketLocation
      const itemMeta = orderItem.itemMeta || {}
      if (!ticketLocation || itemMeta.parter) return []

      const isSpecialTableEvent = SPECIAL_TABLE_LOCATION_EVENT_IDS.includes(Number(this.event.id))
      // Item 26836 is sold as a regular seat, but its row identifies a table.
      const displayRowAsTable =
        isSpecialTableEvent || TABLE_ROW_LOCATION_ORDER_ITEM_IDS.includes(Number(orderItem.id))
      const isTableTicket = itemMeta.table
      const seats = []

      ticketLocation.split('_').forEach(item => {
        let [key, ...value] = item.split('-')
        if (displayRowAsTable && key === 'Seat') return
        if (displayRowAsTable && key === 'Row') {
          key = this.lang !== 'en' ? 'Stol' : 'Table'
        }
        if (this.lang !== 'en') {
          if (key === 'Seat') key = isTableTicket ? 'Stol' : 'Sjedalo'
          if (key === 'Row') key = 'Red'
          if (key === 'Section') key = 'Sektor'
        } else if (key === 'Seat') {
          key = isTableTicket ? 'Table' : key
        }
        seats.push({ key, value: value.join('-') })
      })

      return seats
    },
    buildVatLine(orderItem) {
      if (!this.org.VATObligor || !this.org.VATPercentage) return ''
      const price = Number(orderItem.price)
      const vatAmount = round(price - price / (1 + Number(this.org.VATPercentage) / 100))

      return `${round(this.org.VATPercentage)}% ${this.tr.vat} (${vatAmount}€)`
    },
    shouldHideTicketPrice(purchase) {
      return HIDDEN_PRICE_PROMO_CODE_IDS.includes(Number(purchase && purchase.promoCodeId))
    },
    validityBadge(orderItem) {
      if (!orderItem.hasDateValidationRange) return { text: null, width: null }
      const text =
        orderItem.validityType === 'SINGLE_DAY' ? this.tr.singleDayTicketShort : this.tr.multipleDayTicketShort

      return { text, width: orderItem.validityType === 'SINGLE_DAY' ? '88px' : '97px' }
    },
    async buildTicket(purchase) {
      const orderItem = purchase.orderItem || {}
      const accreditation = purchase.accreditation || {}
      const showPrice = !this.shouldHideTicketPrice(purchase)

      const baseFrom = orderItem.hasDateValidationRange ? orderItem.entryFrom : this.event.from
      const badge = this.validityBadge(orderItem)
      const showEntryTo =
        orderItem.hasDateValidationRange && orderItem.validityType === 'MULTIPLE_DAYS' && orderItem.entryTo

      let qr = ''
      if (accreditation.qrcode) {
        try {
          qr = await QRCode.toDataURL(accreditation.qrcode, { margin: 0 })
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('QR generation failed', err)
        }
      }

      return {
        accreditationId: accreditation.id || purchase.id || 'N/A',
        qr,
        orderItem,
        ownerName: this.buildOwnerName(accreditation),
        seatInfo: this.buildSeatInfo(orderItem, accreditation),
        showPrice,
        vatLine: showPrice ? this.buildVatLine(orderItem) : '',
        nameFontSize: this.calculateNameFontSize(orderItem.name),
        validityBadge: badge.text,
        validityBadgeWidth: badge.width,
        entryFromDate: this.formatDate(baseFrom),
        entryFromTime: this.formatTime(baseFrom),
        showEntryTo,
        entryToDate: showEntryTo ? this.formatDate(orderItem.entryTo) : '',
        entryToTime: showEntryTo ? this.formatTime(orderItem.entryTo) : '',
      }
    },
  },
  apollo: {
    getOrderInfoByUUID: {
      query: GET_ORDER_INFO_BY_UUID,
      variables() {
        return { uuid: this.orderUUID }
      },
      async result({ data, loading }) {
        if (loading) return
        const order = data.getOrderInfoByUUID
        if (!order) return

        this.orderInfo = order
        this.event = order.event || {}
        this.org = (order.event && order.event.organization) || {}
        this.location = (order.event && order.event.location) || {}
        this.lang = this.org.preferredLanguage === 'en' ? 'en' : 'hr'
        moment.locale(this.lang)

        const purchases =
          this.purchaseIndex === 'all'
            ? order.purchases
            : [order.purchases[Number(this.purchaseIndex)]].filter(Boolean)

        this.ticketsData = await Promise.all(purchases.map(p => this.buildTicket(p)))
      },
    },
  },
}
</script>

<style scoped>
.ticket-viewer {
  background: #e6f1f8;
  min-height: 100vh;
  padding: 24px 16px;
  display: flex;
  justify-content: center;
  overflow-x: hidden;
}

.tickets {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.ticket-page {
  width: 595px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(2, 119, 189, 0.12);
  overflow: hidden;
  color: #333;
  font-family: 'Source Sans Pro', 'Raleway', sans-serif;
  /* Scale whole ticket to fit narrow screens (var set inline, defaults to 1).
     Print overrides this with a fixed A4 zoom below. */
  zoom: var(--screen-zoom, 1);
}

b,
.b {
  font-weight: 700;
}

/* FRONT */
.front {
  padding: 17px 32px 12px 32px;
  height: 203px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
}

.front-top {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}

.logo-big {
  width: 75px;
  height: 35px;
  object-fit: contain;
}

.header-data {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 9px;
}

.hd {
  color: #0277bd;
  text-align: right;
  font-size: 8px;
  line-height: 150%;
  text-transform: uppercase;
  letter-spacing: 0.24px;
  white-space: nowrap;
}

.title-block {
  margin-top: 12px;
}

.validity-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 15px;
  border-radius: 8px;
  background: #0277bd;
  color: #fff;
  font-family: 'Raleway', sans-serif;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  box-sizing: border-box;
}

.event-title {
  color: #0277bd;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 800;
  line-height: 120%;
  letter-spacing: -0.6px;
  max-width: 440px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.qr {
  width: 75px;
  height: 75px;
  margin-top: 7px;
  flex-shrink: 0;
}

.front-bottom {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
}

.organizer-block {
  width: 440px;
}

.organizer-label {
  color: #333;
  font-size: 8px;
  line-height: 150%;
  text-transform: uppercase;
}

.organizer-data {
  color: #0277bd;
  font-size: 10px;
  line-height: 130%;
  max-height: 26px;
  overflow: hidden;
  margin-top: 2px;
}

.organizer-data b {
  font-weight: 700;
}

.vat-msg {
  color: #333;
  font-size: 6px;
  line-height: 120%;
  margin-top: 4px;
}

.coreevent-info {
  color: #333;
  font-size: 6px;
  line-height: 120%;
  width: 75px;
  text-align: left;
}

/* DIVIDER */
.divider {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0 0 0 0;
}

.divider-start {
  width: 24px;
  border-top: 1px dashed #0277bd;
}

.divider-middle {
  flex: 1;
  border-top: 1px dashed #0277bd;
}

.divider-text {
  color: #0277bd;
  font-size: 7px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  white-space: nowrap;
}

.divider-circle {
  width: 8px;
  height: 16px;
  background: #e6f1f8;
  border-radius: 0 8px 8px 0;
  margin-right: -32px;
}

/* BACK */
.back {
  height: 194px;
  padding: 0 32px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  box-sizing: border-box;
}

.cover {
  height: 145px;
  width: 258px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.back-data {
  display: flex;
  flex-direction: column;
  width: 257px;
  justify-content: space-evenly;
  height: 100%;
  flex: 1;
}

.back-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  box-sizing: border-box;
}

.left-border {
  padding-left: 8px;
  border-left: 0.5px solid #0277bd;
}

.text-normal {
  color: #333;
  font-size: 10px;
  line-height: 120%;
}

.text-bold {
  color: #333;
  font-size: 12px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: -0.36px;
}

.text-owner {
  font-weight: 400;
}

.text-label {
  color: #333;
  font-size: 8px;
  line-height: 150%;
}

.text-small {
  color: #333;
  font-size: 6px;
  line-height: 120%;
}

.clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* INNER */
.inner {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px 32px 0 32px;
  min-height: 352px;
  box-sizing: border-box;
}

.inner-top {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.how-to-enter {
  display: flex;
  flex-direction: row;
  padding: 8px 14px 11px 14px;
  gap: 12px;
  border-radius: 8px;
  background: #f0f6fa;
  align-items: center;
}

.hte-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 225px;
}

.hte-title,
.hte-list {
  color: #0277bd;
  font-size: 9px;
  line-height: 110%;
}

.hte-title {
  text-transform: uppercase;
}

.hte-list {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.hte-list b {
  font-weight: 700;
}

.hte-link {
  color: #0277bd;
  font-weight: 700;
  text-decoration: underline;
}

.scan-visual {
  width: 266px;
  height: 51px;
  object-fit: contain;
}

.messages {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.org-msg-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.org-msg {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 258px;
}

.org-msg-title {
  color: #333;
  font-size: 9px;
  line-height: 110%;
  text-transform: uppercase;
}

.org-msg-content {
  color: #0277bd;
  font-size: 9px;
  line-height: 110%;
  max-height: 149px;
  overflow: hidden;
}

.terms {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.terms-title {
  color: #333;
  font-size: 9px;
  line-height: 110%;
  text-transform: uppercase;
}

.terms-text {
  color: #333;
  font-size: 6px;
  line-height: 120%;
  text-align: justify;
}

.coreevent-msg {
  color: #0277bd;
  font-size: 9px;
  font-weight: 700;
  line-height: 110%;
}

.additional-visual {
  width: 100%;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  margin: 8px 0;
}

/* FOOTER */
.footer {
  padding: 16px 32px 23px 32px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.footer-core {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: #0277bd;
  font-size: 8px;
}

.logo-small {
  width: 16px;
  height: 14px;
  object-fit: contain;
}

.footer-core .b {
  font-weight: 700;
}

.footer-link {
  color: #0277bd;
  text-decoration: none;
}

.footer-right {
  color: #0277bd;
  font-size: 8px;
  line-height: 150%;
}

.download-fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 100;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  appearance: none;
  border: none;
  cursor: pointer;
  background: #0277bd;
  color: #fff;
  font-family: 'Raleway', 'Source Sans Pro', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  padding: 14px 22px;
  border-radius: 999px;
  box-shadow: 0 6px 20px rgba(2, 119, 189, 0.35);
  transition: background 0.15s ease, transform 0.15s ease;
}

.download-fab__icon {
  flex-shrink: 0;
}

.download-fab:hover {
  background: #026aa8;
  transform: translateY(-1px);
}

.download-fab:active {
  transform: translateY(0);
}

.download-fab:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .ticket-viewer {
    background: #fff;
    padding: 0;
    display: block;
  }
  .download-fab {
    display: none;
  }
  .tickets {
    gap: 0;
  }
  .ticket-page {
    /* design authored in pt-equivalent px (react-pdf A4 = 595pt wide);
       zoom 96/72 scales it to a true A4 page (210mm x 297mm at 96dpi). */
    zoom: 1.3333;
    box-shadow: none;
    border-radius: 0;
    page-break-after: always;
    break-after: page;
  }
}

@media (max-width: 640px) {
  .ticket-viewer {
    padding: 12px;
  }
  .download-fab {
    right: 16px;
    bottom: 16px;
  }
}
</style>
